import React, { useId } from 'react';
import { Box, Flex, Text, Link, Button } from '@chakra-ui/react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { LayoutGroup } from 'motion/react';
import { MotionBox, MotionFlex } from '@components/ui/motion';
import { fadeUp, listStagger, springs } from '@utils/motion';
import { NavigationSection } from './types';

interface NavigationContentProps {
  sections: NavigationSection[];
  onNavigate?: () => void;
  animateItems?: boolean;
}

export const NavigationContent: React.FC<NavigationContentProps> = ({ sections, onNavigate, animateItems = false }) => {
  const location = useLocation();
  const layoutGroupId = useId();

  const isActive = (path: string): boolean => {
    if (path === '/dashboard') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <LayoutGroup id={layoutGroupId}>
      <MotionFlex
        direction="column"
        gap={6}
        w="100%"
        variants={listStagger}
        initial={animateItems ? 'initial' : false}
        animate="animate"
      >
        {sections.map((section, sectionIndex) => (
          <Box key={sectionIndex}>
            {section.title && (
              <Text
                fontSize="xs"
                fontWeight="semibold"
                color="whiteAlpha.600"
                textTransform="uppercase"
                letterSpacing="wider"
                mb={2}
                px={3}
              >
                {section.title}
              </Text>
            )}

            <Flex direction="column" gap={2}>
              {section.items.map((item) => {
                const active = isActive(item.path);
                const Icon = item.icon;
                const soonAvailable = item?.soon ?? false;

                return (
                  <MotionBox key={item.path} variants={fadeUp} position="relative">
                    {active && (
                      <MotionBox
                        layoutId="nav-active"
                        position="absolute"
                        inset={0}
                        bg="white"
                        borderRadius="md"
                        transition={springs.snappy}
                      />
                    )}
                    <Link
                      asChild
                      style={{ textDecoration: 'none' }}
                      opacity={soonAvailable ? '0.8' : '1'}
                      position="relative"
                    >
                      <RouterLink
                        to={soonAvailable ? '#' : item.path}
                        onClick={(event) => {
                          if (soonAvailable) {
                            event.preventDefault();
                            event.stopPropagation();
                          }
                        }}
                      >
                        <Button
                          variant="ghost"
                          bg="transparent"
                          color={active ? '#050e3a' : 'white'}
                          transition="color 0.2s"
                          _hover={{
                            bg: active ? 'transparent' : 'whiteAlpha.200',
                          }}
                          justifyContent="flex-start"
                          width="full"
                          p={2}
                          cursor={soonAvailable ? 'default' : 'pointer'}
                          onClick={onNavigate}
                        >
                          <Flex direction="row" alignItems="center" gap={3}>
                            <Icon style={{ width: '20px', height: '20px' }} />
                            {item.label}
                            {
                              soonAvailable && <span className="nav-coming-soon-badge">Bientôt</span>
                            }
                          </Flex>
                        </Button>
                      </RouterLink>
                    </Link>
                  </MotionBox>
                );
              })}
            </Flex>
          </Box>
        ))}
      </MotionFlex>
    </LayoutGroup>
  );
};
